import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { Icon, toast } from '@kontentino/ui';
import { ActionContext, createAction } from 'actions';
import i18n from 'app/utils/i18n';
import { CognitoTokenManager } from 'app/modules/auth/utils/cognitoTokenManager';
import config from 'config';
import { MediaAttachmentSize } from 'types/Attachments';
import { Post } from 'types/Post';
import { PostDetail } from 'types/PostDetail';
import FilesUtil from 'utils/files';
import { createSearchFromParams } from 'utils/url';
import { getCardMedia } from 'app/modules/posts/utils/cardMedia';

type PostTypes =
  | Pick<PostDetail, 'attachments'>
  | Pick<Post, 'preview_image'>
  | undefined;

function isPostDetail(post: PostTypes): post is PostDetail {
  if (!post) return false;
  return 'attachments' in (post as PostDetail);
}

function isPost(post: PostTypes): post is Post {
  if (!post) return false;
  return 'preview_image' in (post as Post);
}

export const downloadPostAssets = (context: ActionContext) =>
  createAction(
    {
      id: 'download-post-assets',
      label: () => i18n.t('downloadPostAssets'),
      icon: <Icon icon={faArrowDownToLine} />,
      isApplicable: (post: PostTypes) => {
        if (isPostDetail(post)) {
          return !!post?.attachments.cards.some(
            (card) => !!getCardMedia(card) || ('asset' in card && !!card.asset),
          );
        }
        if (isPost(post)) {
          return !!post?.preview_image;
        }
        return false;
      },
      perform: async ({
        id,
        revisionId,
        size = 'original',
      }: {
        id: PostDetail['id'];
        revisionId?: number;
        size?: MediaAttachmentSize;
      }) => {
        const toastId = 'download-assets';
        const searchParams = createSearchFromParams({
          revisionId,
          size,
        });

        const accountId = CognitoTokenManager.getSelectedAccountId();

        context.toast(i18n.t('preparingDownload'), 'default', {
          id: toastId,
          duration: Infinity,
        });

        try {
          await FilesUtil.downloadFromUrl(
            `${config.API_SERVICE_ENDPOINT}/posts/${id}/downloadAssets${searchParams}`,
            {
              headers: {
                Authorization: `Bearer ${context.appStore.authToken}`,
                'x-http-jwt-token': context.appStore.authToken ?? '',
                ...(accountId && { 'X-Account-Id': String(accountId) }),
              },
            },
          );
          toast.dismiss(toastId);
        } catch {
          toast.dismiss(toastId);
          context.toast(i18n.t('downloadFailed'), 'error');
        }
      },
    },
    context,
  );
